import { useUserInfo } from "@/api/user";
import { ROUTES } from "@/constants/routes";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import type { Flags } from "@/providers/LaunchDarklyProvider";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { matchPath, useLocation } from "react-router-dom";
import { useFirstRunPresent } from "../../first-run";
import { useStartState } from "../entry";
import { useOnboardingRecord } from "../status";
import { readProgress } from "../tour/progress";
import { DEFAULT_TOUR } from "../tours";
import { Coachmark } from "./Coachmark";
import { TargetRing } from "./TargetRing";
import "./coachmarks.css";
import { DOCS_BASE, MOMENTS } from "./moments";
import { momentsFor, useCoachRequest } from "./request";
import { position, useCoachRunner, type Active } from "./runner";
import { seenSet } from "./seen";
import { useSelectorRects, useTargetRect } from "./targets";
import { STEP_TINTS, type Moment, type ShellFacts } from "./types";

const inField = (t: EventTarget | null): boolean =>
  t instanceof HTMLElement &&
  (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);

/**
 * Enter reaches the layer by intent: the body or the card root itself. A
 * button or link on the card that has focus activates on its own.
 */
const ours = (t: EventTarget | null): boolean =>
  !(t instanceof Element) ||
  t === document.body ||
  t.matches("[data-coach-layer] .cm-card");

/** Sequences take the step's paper by live position; one card at step 1 is butter. */
const tintOf = (m: Moment, step: number) =>
  m.cards.length > 1
    ? STEP_TINTS[(step - 1) % STEP_TINTS.length]
    : (m.tint ?? "butter");

/**
 * The shell mount: active with the `coachmarks` flag, or while a flow has
 * asked for a moment by name (the first run's landing), on lg, once the
 * record says the welcome dialog and the tour are behind the user. One portal
 * to the body carries the ring and the card above every piece of chrome;
 * pointer-downs stop here so a Radix dialog beneath does not read a click on
 * the card as a click outside.
 */
export function CoachmarkLayer() {
  const flag = !!useFlags<Flags>().coachmarks;
  const request = useCoachRequest();
  const lg = useBreakpoint("lg");
  const { record, stage } = useOnboardingRecord();
  const enabled =
    (flag || !!request) && lg && (stage === "skipped" || stage === "done");
  const { pathname } = useLocation();

  // a requested moment walks the cards the request names
  const moments = useMemo(() => momentsFor(MOMENTS, request), [request]);
  const seen = useMemo(() => seenSet(record, moments), [record, moments]);
  const { data: userInfo } = useUserInfo();
  const usageMeter = !!userInfo?.subscription && !!userInfo?.usage;
  const facts = useMemo<ShellFacts>(
    () => ({
      pathname,
      canvasId:
        matchPath(ROUTES.CANVAS.DETAIL, pathname)?.params.canvasId ?? null,
      tourCanvasId: readProgress(record, DEFAULT_TOUR)?.canvasId ?? null,
      stage,
      usageMeter,
    }),
    [pathname, record, stage, usageMeter],
  );
  const runner = useCoachRunner({
    enabled,
    moments: moments,
    pathname,
    seen,
    facts,
    request,
  });

  const active: Active | null = enabled ? runner.active : null;
  const moment = active
    ? (moments.find((m) => m.key === active.moment) ?? null)
    : null;
  const card = active && moment ? (moment.cards[active.card] ?? null) : null;
  // Positions count only the cards that apply: no "1 of 2" before a card
  // that will be passed over, and Done where nothing else will show.
  const place =
    active && moment && card ? position(moment, active.card, facts) : null;
  const beat =
    active?.phase.kind === "beat-hold" || active?.phase.kind === "beat-settle";
  const showing = active?.phase.kind === "showing";

  const cardTarget = useTargetRect(card ? card.target : null);
  const beatTarget = useTargetRect(
    card && beat ? (card.before?.target ?? null) : null,
  );
  const clearAnchor = useTargetRect(card?.clear ?? null).rect;
  const clearRects = useSelectorRects(card?.clearSelector ?? null);
  const clear = clearAnchor ?? clearRects;
  const ringAt = beat ? beatTarget : cardTarget;
  // A modal, menu or select is open over the target, a tour start is in
  // flight (its failure scrim covers the window above this layer), or the
  // first run's layer is up over everything: the layer hides as one and
  // comes back as the same card, nothing unmounted, no key taken.
  const starting = useStartState() !== "idle";
  const firstRun = useFirstRunPresent();
  const occluded = ringAt.presence === "occluded" || starting || firstRun;

  // One pulse per landing: the ring's target, not its rect, marks an arrival.
  const ringTarget = card ? (beat ? card.before?.target : card.target) : null;
  const [arrival, setArrival] = useState(0);
  useEffect(() => {
    if (ringTarget) setArrival((n) => n + 1);
  }, [ringTarget]);

  // Enter is Next or Done, Escape is Skip, while the card is up. Nothing
  // fires from inside a field, and Enter only when nothing in the product
  // holds focus, so a focused control under the card keeps its own keys.
  // Under occlusion the keys are the modal's: Radix prevents the Escape it
  // closes on, and the card ignores the rest.
  const keysRef = useRef({ showing, last: false, runner });
  keysRef.current = {
    showing: showing && !occluded,
    last: !!place?.last,
    runner,
  };
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e: KeyboardEvent) => {
      const { showing, last, runner } = keysRef.current;
      if (!showing || e.defaultPrevented || inField(e.target)) return;
      if (e.key === "Escape") {
        e.preventDefault();
        runner.skip();
        return;
      }
      if (e.key === "Enter" && !e.metaKey && !e.ctrlKey && ours(e.target)) {
        e.preventDefault();
        (last ? runner.done : runner.next)();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);

  if (!enabled || !active || !moment || !card) return null;
  const docs =
    DOCS_BASE && card.docs
      ? { href: `${DOCS_BASE}/${card.docs.path}`, label: card.docs.label }
      : null;

  return createPortal(
    <div
      data-coach-layer=""
      data-occluded={occluded ? "" : undefined}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <TargetRing rect={ringAt.rect} arrival={arrival} />
      {!beat && (
        <Coachmark
          id={`${moment.key}.${card.key}`}
          tint={tintOf(moment, place!.step)}
          icon={card.icon}
          rect={cardTarget.rect}
          clear={clear}
          side={card.side}
          align={card.align}
          step={place!.step}
          count={place!.count}
          title={card.title}
          body={card.body}
          figure={card.figure}
          keys={card.keys}
          docs={docs}
          onNext={runner.next}
          onSkip={runner.skip}
          onDone={runner.done}
        />
      )}
    </div>,
    document.body,
  );
}
